import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Box, Typography, TextField, IconButton } from '@mui/material'
import dayjs from 'dayjs'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '@/shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '@/constants/colors'
import { Button } from '@/interaxDesignSystemComponents'
import { calculateEndTime, formatDate, formatTime, getNextAvailableSlot } from '../../utils'
import { parseDateStringToYMD, buildDateStringFromYMD, getTodayYMD } from './calendarDateHelpers'
import { TESTIDS } from '../../../../../config/testids'

const CALENDAR_PICKER_KEYS = 'facilitator.trainingSprint.scheduleSessions.calendarPicker'

const WEEKDAY_KEYS = [
  'facilitator.trainingSprint.scheduleSessions.calendarPicker.weekdaySun',
  'facilitator.trainingSprint.scheduleSessions.calendarPicker.weekdayMon',
  'facilitator.trainingSprint.scheduleSessions.calendarPicker.weekdayTue',
  'facilitator.trainingSprint.scheduleSessions.calendarPicker.weekdayWed',
  'facilitator.trainingSprint.scheduleSessions.calendarPicker.weekdayThu',
  'facilitator.trainingSprint.scheduleSessions.calendarPicker.weekdayFri',
  'facilitator.trainingSprint.scheduleSessions.calendarPicker.weekdaySat'
]

const inputSx = {
  backgroundColor: INTERAX_COLORS.heritageBlue40,
  '& .MuiOutlinedInput-root': {
    color: INTERAX_COLORS.white,
    borderColor: INTERAX_COLORS.heritageBlue,
    '& fieldset': { borderColor: INTERAX_COLORS.heritageBlue },
    '&:hover fieldset': { borderColor: INTERAX_COLORS.heritageBlue }
  }
}

export interface CalendarPickerProps {
  isOpen: boolean
  onClose: () => void
  onDateSelect: (date: string, time: string, endTime: string) => void
  activityDuration: number // in minutes
  initialDate?: string
  initialTime?: string
  previousActivity?: { scheduledDate?: string; endTime?: string }
  className?: string
}

function getInitialYMD(initialDate?: string) {
  if (initialDate) {
    return parseDateStringToYMD(initialDate)
  }
  return getTodayYMD()
}

export const CalendarPicker: React.FC<CalendarPickerProps> = ({
  isOpen,
  onClose,
  onDateSelect,
  activityDuration,
  initialDate,
  initialTime,
  previousActivity,
  className = ''
}) => {
  const { t } = useTranslation()
  const initialYMD = getInitialYMD(initialDate)
  const [year, setYear] = useState(initialYMD.year)
  const [month, setMonth] = useState(initialYMD.month)
  const [day, setDay] = useState(initialYMD.day)
  const [selectedTime, setSelectedTime] = useState(initialTime || '09:00')

  const monthInputRef = useRef<HTMLInputElement>(null)
  const yearInputRef = useRef<HTMLInputElement>(null)
  const timeInputRef = useRef<HTMLInputElement>(null)

  const selectedDate = buildDateStringFromYMD(year, month, day)
  const today = getTodayYMD()
  const todayString = buildDateStringFromYMD(today.year, today.month, today.day)
  const isDateInPast = selectedDate !== '' && selectedDate < todayString
  const canConfirm = selectedDate !== '' && selectedTime !== '' && !isDateInPast

  // Calendar view month (for prev/next navigation)
  const viewDate = useMemo(() => {
    if (year.length === 4 && month.length === 2) {
      const m = parseInt(month, 10)
      const y = parseInt(year, 10)
      if (m >= 1 && m <= 12) return dayjs(`${y}-${month}-01`)
    }
    return dayjs()
  }, [year, month])

  const calendarGrid = useMemo(() => {
    const start = viewDate.startOf('month')
    const daysInMonth = viewDate.daysInMonth()
    const firstWeekday = start.day()
    const cells: (number | null)[] = []
    for (let i = 0; i < firstWeekday; i++) cells.push(null)
    for (let d = 1; d <= daysInMonth; d++) cells.push(d)
    while (cells.length % 7 !== 0) cells.push(null)
    return cells
  }, [viewDate])

  const handleCalendarDayClick = useCallback(
    (dayOfMonth: number) => {
      const d = viewDate.date(dayOfMonth)
      setDay(d.format('DD'))
      setMonth(d.format('MM'))
      setYear(d.format('YYYY'))
    },
    [viewDate]
  )

  const goPrevMonth = useCallback(() => {
    const next = viewDate.subtract(1, 'month')
    setMonth(next.format('MM'))
    setYear(next.format('YYYY'))
  }, [viewDate])

  const goNextMonth = useCallback(() => {
    const next = viewDate.add(1, 'month')
    setMonth(next.format('MM'))
    setYear(next.format('YYYY'))
  }, [viewDate])

  useEffect(() => {
    if (!isOpen) return
    if (initialDate) {
      const parsed = parseDateStringToYMD(initialDate)
      setYear(parsed.year)
      setMonth(parsed.month)
      setDay(parsed.day)
    }
    if (initialTime) {
      setSelectedTime(initialTime)
    }
  }, [isOpen, initialDate, initialTime])

  const handleConfirm = useCallback(() => {
    if (canConfirm) {
      const endTime = calculateEndTime(selectedTime, activityDuration)
      onDateSelect(selectedDate, selectedTime, endTime)
      onClose()
    }
  }, [canConfirm, selectedDate, selectedTime, activityDuration, onDateSelect, onClose])

  const applyDateTime = useCallback((date: string, time: string) => {
    const d = dayjs(date)
    setYear(d.format('YYYY'))
    setMonth(d.format('MM'))
    setDay(d.format('DD'))
    setSelectedTime(time)
  }, [])

  const handleNowPlusOne = useCallback(() => {
    const next = dayjs().add(1, 'minute')
    applyDateTime(next.format('YYYY-MM-DD'), next.format('HH:mm'))
  }, [applyDateTime])

  const handleNextSlot = useCallback(() => {
    const { date, time } = getNextAvailableSlot(previousActivity)
    applyDateTime(date, time)
  }, [previousActivity, applyDateTime])

  const restrictToDigits = (value: string, maxLength: number) => value.replace(/\D/g, '').slice(0, maxLength)

  const handleDayChange = (value: string) => {
    const next = restrictToDigits(value, 2)
    const valid =
      next === '' ||
      (next.length === 1 && next >= '0' && next <= '9') ||
      (next.length === 2 && parseInt(next, 10) >= 1 && parseInt(next, 10) <= 31)
    if (valid) {
      setDay(next)
      if (next.length === 2) {
        monthInputRef.current?.focus()
      }
    }
  }

  const handleMonthChange = (value: string) => {
    const next = restrictToDigits(value, 2)
    const valid =
      next === '' ||
      (next.length === 1 && next >= '0' && next <= '9') ||
      (next.length === 2 && parseInt(next, 10) >= 1 && parseInt(next, 10) <= 12)
    if (valid) {
      setMonth(next)
      if (next.length === 2) {
        yearInputRef.current?.focus()
      }
    }
  }

  const handleYearChange = (value: string) => {
    const next = restrictToDigits(value, 4)
    setYear(next)
    if (next.length === 4) {
      timeInputRef.current?.focus()
    }
  }

  const isDayDisabled = useCallback(
    (dayOfMonth: number) => {
      const d = viewDate.date(dayOfMonth)
      return d.isBefore(dayjs().startOf('day'))
    },
    [viewDate]
  )

  const isDaySelected = useCallback(
    (dayOfMonth: number) => {
      if (day.length !== 2 || month.length !== 2 || year.length !== 4) return false
      return (
        parseInt(day, 10) === dayOfMonth &&
        viewDate.month() + 1 === parseInt(month, 10) &&
        viewDate.year() === parseInt(year, 10)
      )
    },
    [day, month, year, viewDate]
  )

  if (!isOpen) return null

  return (
    <Box
      data-testid="calendar-picker-modal"
      className={`${className}`}
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50
      }}
    >
      <Box
        sx={{
          backgroundColor: INTERAX_COLORS.offBlack,
          borderRadius: 2,
          p: 3,
          width: '100%',
          maxWidth: { xs: '100%', sm: 400 },
          mx: 2
        }}
      >
        <Typography
          component="h3"
          sx={{
            color: INTERAX_COLORS.white,
            fontFamily: INTERAX_TYPOGRAPHY.headline4.fontFamily,
            fontWeight: INTERAX_TYPOGRAPHY.headline4.fontWeight,
            fontSize: INTERAX_TYPOGRAPHY.headline4.fontSize,
            lineHeight: INTERAX_TYPOGRAPHY.headline4.lineHeight,
            mb: 2
          }}
        >
          {t(`${CALENDAR_PICKER_KEYS}.title`)}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Calendar grid */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              backgroundColor: INTERAX_COLORS.heritageBlue40
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <IconButton
                size="small"
                onClick={goPrevMonth}
                aria-label={t(`${CALENDAR_PICKER_KEYS}.prevMonth`)}
                sx={{ color: INTERAX_COLORS.white }}
              >
                <ChevronLeftIcon style={{ width: 20, height: 20 }} />
              </IconButton>
              <Typography
                sx={{
                  color: INTERAX_COLORS.white,
                  fontFamily: INTERAX_TYPOGRAPHY.body2.fontFamily,
                  fontWeight: 600,
                  fontSize: INTERAX_TYPOGRAPHY.body2.fontSize
                }}
              >
                {viewDate.format('MMMM YYYY')}
              </Typography>
              <IconButton
                size="small"
                onClick={goNextMonth}
                aria-label={t(`${CALENDAR_PICKER_KEYS}.nextMonth`)}
                sx={{ color: INTERAX_COLORS.white }}
              >
                <ChevronRightIcon style={{ width: 20, height: 20 }} />
              </IconButton>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, textAlign: 'center' }}>
              {WEEKDAY_KEYS.map((key) => (
                <Typography
                  key={key}
                  sx={{
                    color: INTERAX_COLORS.thunderGrey,
                    fontSize: 11,
                    fontWeight: 600,
                    py: 0.5
                  }}
                >
                  {t(key)}
                </Typography>
              ))}
              {calendarGrid.map((cell, idx) => {
                if (cell === null) {
                  return <Box key={`empty-${idx}`} sx={{ py: 0.75 }} />
                }
                const disabled = isDayDisabled(cell)
                const selected = isDaySelected(cell)
                return (
                  <Box
                    key={`${viewDate.format('YYYY-MM')}-${cell}`}
                    component="button"
                    type="button"
                    onClick={() => !disabled && handleCalendarDayClick(cell)}
                    disabled={disabled}
                    sx={{
                      border: 'none',
                      borderRadius: 1,
                      py: 0.75,
                      fontSize: 14,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      backgroundColor: selected ? INTERAX_COLORS.sun : 'transparent',
                      color: selected ? INTERAX_COLORS.offBlack : INTERAX_COLORS.white,
                      opacity: disabled ? 0.4 : 1,
                      '&:hover': disabled
                        ? {}
                        : { backgroundColor: selected ? INTERAX_COLORS.sun : INTERAX_COLORS.heritageBlue }
                    }}
                  >
                    {cell}
                  </Box>
                )
              })}
            </Box>
          </Box>

          {/* Date inputs: DD MM YYYY */}
          <Box>
            <Typography
              component="label"
              sx={{
                display: 'block',
                color: INTERAX_COLORS.typographyLabel,
                fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
                fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
                fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
                lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight,
                mb: 1
              }}
            >
              {t(`${CALENDAR_PICKER_KEYS}.date`)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder={t(`${CALENDAR_PICKER_KEYS}.day`)}
                value={day}
                onChange={(e) => handleDayChange(e.target.value)}
                inputProps={{
                  maxLength: 2,
                  inputMode: 'numeric',
                  'aria-label': t(`${CALENDAR_PICKER_KEYS}.day`)
                }}
                sx={{ ...inputSx, flex: 0.8 }}
              />
              <TextField
                inputRef={monthInputRef}
                size="small"
                placeholder={t(`${CALENDAR_PICKER_KEYS}.month`)}
                value={month}
                onChange={(e) => handleMonthChange(e.target.value)}
                inputProps={{
                  maxLength: 2,
                  inputMode: 'numeric',
                  'aria-label': t(`${CALENDAR_PICKER_KEYS}.month`)
                }}
                sx={{ ...inputSx, flex: 0.8 }}
              />
              <TextField
                inputRef={yearInputRef}
                size="small"
                placeholder={t(`${CALENDAR_PICKER_KEYS}.year`)}
                value={year}
                onChange={(e) => handleYearChange(e.target.value)}
                inputProps={{
                  maxLength: 4,
                  inputMode: 'numeric',
                  'aria-label': t(`${CALENDAR_PICKER_KEYS}.year`)
                }}
                sx={{ ...inputSx, flex: 1.2 }}
              />
            </Box>
          </Box>

          <Box>
            <Typography
              component="label"
              sx={{
                display: 'block',
                color: INTERAX_COLORS.typographyLabel,
                fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
                fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
                fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
                lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight,
                mb: 1
              }}
            >
              {t(`${CALENDAR_PICKER_KEYS}.startTime`)}
            </Typography>
            <TextField
              inputRef={timeInputRef}
              type="time"
              size="small"
              fullWidth
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              inputProps={{
                'aria-label': t(`${CALENDAR_PICKER_KEYS}.startTime`),
                'data-testid': 'calendar-time-input'
              }}
              sx={inputSx}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Box
                component="button"
                type="button"
                data-testid={TESTIDS.CALENDAR_NOW_BTN}
                onClick={handleNowPlusOne}
                sx={{
                  flex: 1,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  border: `1px solid ${INTERAX_COLORS.sun}`,
                  backgroundColor: 'transparent',
                  color: INTERAX_COLORS.sun,
                  cursor: 'pointer',
                  fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
                  fontWeight: 600,
                  fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
                  lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight,
                  '&:hover': { backgroundColor: INTERAX_COLORS.sun20 }
                }}
              >
                {t(`${CALENDAR_PICKER_KEYS}.nowPlusOne`, 'Now +1 min')}
              </Box>
              <Box
                component="button"
                type="button"
                data-testid={TESTIDS.CALENDAR_NEXT_SLOT_BTN}
                onClick={handleNextSlot}
                sx={{
                  flex: 1,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  border: `1px solid ${INTERAX_COLORS.sun}`,
                  backgroundColor: 'transparent',
                  color: INTERAX_COLORS.sun,
                  cursor: 'pointer',
                  fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
                  fontWeight: 600,
                  fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
                  lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight,
                  '&:hover': { backgroundColor: INTERAX_COLORS.sun20 }
                }}
              >
                {t(`${CALENDAR_PICKER_KEYS}.nextSlot`, 'Next slot')}
              </Box>
            </Box>
          </Box>

          {selectedDate && selectedTime && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: INTERAX_COLORS.heritageBlue40
              }}
            >
              <Typography
                sx={{
                  color: INTERAX_COLORS.thunderGrey,
                  fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
                  fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
                  fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
                  lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight
                }}
              >
                {formatDate(selectedDate)} • {formatTime(selectedTime)} –{' '}
                {formatTime(calculateEndTime(selectedTime, activityDuration))}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
          <Button variant="secondary" onClick={onClose} fullWidth>
            {t('common.cancel')}
          </Button>
          <Box
            component="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="calendar-confirm-btn"
            sx={{
              flex: 1,
              px: 2,
              py: 1.25,
              borderRadius: 1,
              fontFamily: INTERAX_TYPOGRAPHY.body1.fontFamily,
              fontWeight: INTERAX_TYPOGRAPHY.body1.fontWeight,
              fontSize: INTERAX_TYPOGRAPHY.body1.fontSize,
              lineHeight: INTERAX_TYPOGRAPHY.body1.lineHeight,
              backgroundColor: INTERAX_COLORS.sun,
              color: INTERAX_COLORS.offBlack,
              border: 'none',
              cursor: canConfirm ? 'pointer' : 'not-allowed',
              opacity: canConfirm ? 1 : 0.5
            }}
          >
            {t(`${CALENDAR_PICKER_KEYS}.schedule`)}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
